import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';

const slotOf = (h) => (h < 12 ? 'matin' : h < 18 ? 'midi' : 'soir');
const keyFor = (uid) => {
  const d = new Date();
  const day = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  return `asa_stuck_alert_${uid}_${day}_${slotOf(d.getHours())}`;
};

// Vidéos « En cours » depuis 3 jours+ : pop-up jusqu'à 3 fois par jour (matin, midi, soir).
export const StuckVideosAlert = ({ userId, nameOf = (id) => id }) => {
  const [videos, setVideos] = useState(null);
  const check = useCallback(async () => {
    if (!userId || document.visibilityState === 'hidden') return;
    try { if (localStorage.getItem(keyFor(userId))) return; } catch { /* noop */ }
    try {
      const r = await api.get('/alerts/stuck-videos');
      if (r.data?.videos?.length) setVideos(r.data.videos);
    } catch { /* noop */ }
  }, [userId]);
  useEffect(() => {
    check();
    const iv = setInterval(check, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', check); };
  }, [check]);
  const close = () => {
    try { localStorage.setItem(keyFor(userId), '1'); } catch { /* noop */ }
    setVideos(null);
  };
  if (!videos) return null;
  return createPortal(
    <div data-testid="stuck-videos-alert" onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(3,6,18,0.72)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, maxHeight: '80vh', overflowY: 'auto', background: 'linear-gradient(180deg, #2a1418 0%, #160b10 100%)', border: '1px solid rgba(255,107,107,0.4)', borderRadius: 20, padding: 22, color: '#F5ECEC', boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <AlertTriangle size={22} color="#FF6B6B" />
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17 }}>Cousin, c’est pas normal</div>
        </div>
        <div style={{ fontFamily: 'Lexend', fontSize: 13, color: '#D9C2C2', lineHeight: 1.5, marginBottom: 14 }}>
          {videos.length > 1 ? `${videos.length} vidéos sont` : 'Cette vidéo est'} en cours depuis 3 jours ou plus. Termine-la ou préviens l’équipe aujourd’hui.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {videos.map((v) => (
            <div key={v.id} data-testid={`stuck-video-${v.id}`} style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.22)' }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }}>{v.title}</div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: '#C9A9A9', marginTop: 2 }}>{v.project_name} · en cours depuis <b style={{ color: '#FF8A8A' }}>{v.days} jours</b>{v.monteur ? ` · ${nameOf(v.monteur)}` : ''}</div>
            </div>
          ))}
        </div>
        <button data-testid="stuck-videos-alert-close" onClick={close} style={{ marginTop: 16, width: '100%', padding: '11px 0', borderRadius: 10, background: '#FF6B6B', color: '#1a0b0e', fontFamily: 'Sora', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}>Compris, je m’en occupe</button>
      </div>
    </div>,
    document.body,
  );
};
